import React, { FC, useEffect, useMemo } from "react";
import { AxiosError } from "axios";
import __ from "src/utils/Translations";
import useFetchUserData from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchUserData";
import { useSaveOrganizationStructureForm } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useSaveOrganizationStructureForm";
import { useFetchStructureData } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchStructureData";
import useFetchArea from "src/Pages/Company/OrganizationStructure/api/useFetchArea";
import { Alert, Button, FormGroup } from "reactstrap";
import InfoIcon from "src/assets/custom/icons/info_icon.svg";
import WarningIcon from "src/assets/custom/icons/warning_icon.svg";

import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { organizationStructureOrganizationStructureTabEdit } from "src/utils/RoleBasedSecurity/permissions";
import {
  cardStyle,
  headerBoxStyle,
  infoTextStyle,
  titleTextStyle,
} from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/forms/style";
import { Box, Typography, Stack, Divider } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import LocalizationsField from "src/Pages/Company/OrganizationStructure/ConfigureStructure/CreateStructure/FormFields/LocalizationsField";
import PositionsField from "src/Pages/Company/OrganizationStructure/ConfigureStructure/CreateStructure/FormFields/PositionsField";
import AreasField from "src/Pages/Company/OrganizationStructure/ConfigureStructure/CreateStructure/FormFields/AreasField";
import ToggleSwitch from "src/Components/FormElements/ToggleSwitch";
import SupervisorsField from "src/Pages/Company/OrganizationStructure/ConfigureStructure/CreateStructure/FormFields/SupervisorsField";
import {
  IEmployeeDataSave,
  ISaveEmployeeForm,
  IEmployeeData,
} from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/Forms/OrganizationStructureForm.models";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import organizationStructureSchema from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/Forms/OrganizationStructureForm.schema";
import { dynamicNotification } from "src/utils/Notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import useFetchPotentialAreasToMembership from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/usePotentialAreasToMembership";

export interface Option {
  value: string;
  label: string;
}

interface OrganizationStructureFormData {
  localization: Option | null;
  position: Option | null;
  area: Option | null;
  additionalArea: Option | null;
  supervisor: Option | null;
  inheritAdditionalDisplayArea: boolean;
  inheritSupervisor: boolean;
}

interface OrganizationStructureFormProps {
  employeeId: string;
  companyId: string;
  setIsEdited?: (isEdited: boolean, callback?: () => void) => void;
}

const OrganizationStructureForm: FC<OrganizationStructureFormProps> = ({
  employeeId,
  companyId,
  setIsEdited,
}) => {
  const isEditable: boolean = usePermission(
    organizationStructureOrganizationStructureTabEdit
  );

  const queryClient = useQueryClient();
  const cachedData = queryClient.getQueryData<IEmployeeData>([
    "fetch-user-form-data",
  ]);

  const { data: employeeData } = useFetchUserData(companyId, employeeId);
  const { mutate, error: saveError } = useSaveOrganizationStructureForm(
    companyId,
    employeeId
  );

  const logStatus = useLogStatus();

  useEffect(() => {
    if (!saveError) return;
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message:
        "OrganizationStructureForm: saveOrganizationStructureForm failed",
      statusDetails: {
        source: "API",
        operation: "saveOrganizationStructureForm",
        status: (saveError as AxiosError)?.response?.status,
        url: (saveError as AxiosError)?.config?.url,
      },
      errorObject:
        saveError instanceof Error ? saveError : new Error(String(saveError)),
    });
  }, [saveError, logStatus]);

  const { data: structureData } = useFetchStructureData(companyId);
  const structureId: string | undefined = structureData?.[0]?.structureId;
  const structureName: string = structureData?.[0]?.name || __("");

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<OrganizationStructureFormData>({
    resolver: standardSchemaResolver(organizationStructureSchema),
    defaultValues: {
      localization: null,
      position: null,
      area: null,
      additionalArea: null,
      supervisor: null,
      inheritAdditionalDisplayArea: false,
      inheritSupervisor: false,
    },
  });

  useEffect(() => {
    if (setIsEdited) {
      setIsEdited(isDirty);
    }
  }, [isDirty, setIsEdited]);

  const isInheritingAdditionalArea = watch("inheritAdditionalDisplayArea");
  const areaWatch = watch("area");
  const isInheritingSupervisor = watch("inheritSupervisor");

  const currentAreaId = areaWatch?.value ?? employeeData?.area?.id;

  const { data: potentialAreasData } = useFetchPotentialAreasToMembership({
    companyId,
    employeeId,
    enabled: !!companyId && !!employeeId && !!currentAreaId,
    filters: `id==${currentAreaId}`,
  });

  const currentAreaMetadata = useMemo(() => {
    const area = potentialAreasData?.pages?.[0]?.content?.[0];
    if (!area) return null;
    return {
      givenEmployeeCanInheritManager: area.givenEmployeeCanInheritManager,
      managedByGivenEmployee: area.managedByGivenEmployee,
    };
  }, [potentialAreasData]);

  const { data: areaData } = useFetchArea({
    companyId,
    structureId,
    areaId: areaWatch?.value,
    enabled: !!companyId && !!structureId && !!areaWatch?.value,
  });

  useEffect(() => {
    if (employeeData) {
      reset({
        localization: employeeData.localization
          ? {
              value: employeeData.localization.id,
              label: employeeData.localization.label,
            }
          : null,
        position: employeeData.position
          ? {
              value: employeeData.position.id,
              label: employeeData.position.label,
            }
          : null,
        area: employeeData.area
          ? {
              value: employeeData.area.id,
              label: employeeData.area.label,
            }
          : null,
        additionalArea: employeeData.additionalArea
          ? {
              value: employeeData.additionalArea.id,
              label: employeeData.additionalArea.label,
            }
          : null,
        supervisor: employeeData.supervisor
          ? {
              value: employeeData.supervisor.employeeId,
              label: `${employeeData.supervisor.name} (FK: ${employeeData.supervisor.fk})`,
            }
          : {
              value: "",
              label: structureName,
            },
        inheritAdditionalDisplayArea: employeeData.inheritAdditionalDisplayArea,
        inheritSupervisor: employeeData.inheritSupervisor,
      });
    }
  }, [employeeData, reset, structureName]);

  useEffect(() => {
    if (!isInheritingAdditionalArea) return;

    if (areaData?.additionalDisplayArea) {
      setValue("additionalArea", {
        value: areaData.additionalDisplayArea.id!,
        label: areaData.additionalDisplayArea.label!,
      });
    } else {
      setValue("additionalArea", null);
    }
  }, [areaData, setValue, isInheritingAdditionalArea]);

  const inheritedSupervisor = useMemo(
    () =>
      areaData?.manager
        ? {
            value: areaData.manager.id,
            label: `${areaData.manager.name} (FK: ${areaData.manager.fk})`,
          }
        : null,
    [areaData]
  );

  useEffect(() => {
    if (!isInheritingSupervisor || !areaWatch?.value || !employeeData) return;

    const isChanged = employeeData.area?.id !== areaWatch.value;
    if (cachedData?.supervisor) {
      setValue("supervisor", {
        value: cachedData.supervisor.employeeId,
        label: `${cachedData.supervisor.name} (FK: ${cachedData.supervisor.fk})`,
      });
    } else {
      setValue("supervisor", {
        value: "",
        label: structureName,
      });
    }

    if (!isChanged) return;

    if (areaData?.manager?.id === employeeId) {
      setValue("supervisor", {
        value: "",
        label: "Przełożony zostanie odziedziczony z nadrzędnego obszaru",
      });
    } else if (
      areaData?.manager?.id !== employeeId &&
      currentAreaMetadata?.givenEmployeeCanInheritManager
    ) {
      setValue("supervisor", {
        value: inheritedSupervisor?.value || "",
        label: inheritedSupervisor?.label || structureName,
      });
    } else {
      setValue("supervisor", {
        value: "",
        label: `${structureName}`,
      });
    }
  }, [
    areaWatch?.value,
    employeeData?.area?.id,
    employeeId,
    inheritedSupervisor,
    isInheritingSupervisor,
    setValue,
    structureName,
    currentAreaMetadata,
    cachedData?.supervisor,
    areaData?.manager?.id,
  ]);

  useEffect(() => {
    if (!isInheritingSupervisor) {
      const currentSupervisor = getValues("supervisor");
      if (
        currentSupervisor?.label ===
        "Przełożony zostanie odziedziczony z nadrzędnego obszaru"
      ) {
        setValue("supervisor", { value: "", label: structureName });
      }
    }
  }, [isInheritingSupervisor, getValues, setValue, structureName]);

  useEffect(() => {
    if (!employeeData) return;

    if (!areaWatch?.value) {
      if (isDirty) {
        setValue("supervisor", { value: "", label: structureName });
      }
      return;
    }

    const isAreaUnchanged = areaWatch.value === employeeData.area?.id;
    if (isAreaUnchanged) return;

    if (currentAreaMetadata === null) return;

    if (!currentAreaMetadata.givenEmployeeCanInheritManager) {
      setValue("inheritSupervisor", false);
    }
  }, [
    areaWatch?.value,
    currentAreaMetadata,
    setValue,
    employeeData,
    isDirty,
    structureName,
  ]);

  const getDataToPayload = (data: ISaveEmployeeForm): IEmployeeDataSave => ({
    positionId: data.position?.value ?? null,
    localizationId: data.localization?.value ?? null,
    areaId: data.area?.value ?? null,
    inheritAdditionalDisplayArea: data.inheritAdditionalDisplayArea ?? false,
    additionalDisplayAreaId: data.additionalArea?.value ?? null,
    inheritSupervisor: data.inheritSupervisor ?? false,
    supervisorId: data.supervisor?.value?.trim() || null,
  });

  const onSubmit = (data: ISaveEmployeeForm) => {
    const payload = getDataToPayload(data);

    mutate(payload as IEmployeeDataSave, {
      onSuccess: () => {
        dynamicNotification(__("Dane zostały zaktualizowane"), "success");

        reset(getValues());

        queryClient.invalidateQueries({
          queryKey: ["fetch-user-form-data"],
        });

        if (setIsEdited) {
          setIsEdited(false);
        }
      },
    });
  };

  return (
    <>
      <Box sx={headerBoxStyle}>
        <Typography sx={titleTextStyle}>
          {__("Struktura organizacji")}
        </Typography>
        <Box>
          <Button
            type="button"
            color="primary"
            size="lg"
            style={{ marginRight: "8px" }}
            onClick={handleSubmit(onSubmit)}
            disabled={!isEditable}
          >
            {__("Zapisz")}
          </Button>
        </Box>
      </Box>
      <Box sx={cardStyle}>
        <Box sx={{ padding: "16px 20px" }}>
          <Typography
            sx={{
              ...infoTextStyle,
              textTransform: "uppercase",
              fontWeight: 700,
              color: "#12154EB2",
            }}
          >
            {__("Informacje o pracowniku w organizacji")}
          </Typography>
        </Box>
        <hr style={{ margin: 0 }} />
        <Box sx={{ padding: "20px" }}>
          <Stack flexDirection="column" gap={2}>
            <FormGroup style={{ marginBottom: 0 }}>
              <Box sx={{ width: "100%" }}>
                <Controller
                  name="localization"
                  control={control}
                  render={({ field }) => (
                    <LocalizationsField
                      structureId={structureId!}
                      companyId={companyId}
                      value={field.value}
                      onChange={field.onChange}
                      isEditable={isEditable}
                    />
                  )}
                />

                <Controller
                  name="position"
                  control={control}
                  render={({ field }) => (
                    <PositionsField
                      structureId={structureId!}
                      companyId={companyId}
                      value={field.value}
                      onChange={field.onChange}
                      isEditable={isEditable}
                    />
                  )}
                />

                <Controller
                  name="area"
                  control={control}
                  render={({ field }) => (
                    <AreasField
                      companyId={companyId}
                      employeeId={employeeId}
                      value={field.value}
                      onChange={field.onChange}
                      isEditable={isEditable}
                      label={__("Obszar")}
                      error={errors.additionalArea}
                    />
                  )}
                />

                <Controller
                  name="inheritAdditionalDisplayArea"
                  control={control}
                  render={({ field }) => (
                    <ToggleSwitch
                      disabled={!isEditable}
                      checked={field.value}
                      handleChange={field.onChange}
                      label={__(
                        "Wyświetlany dodatkowy obszar jest pobierany z ustawień obszaru, do którego należy pracownik"
                      )}
                    />
                  )}
                />

                <Controller
                  name="additionalArea"
                  control={control}
                  render={({ field }) => (
                    <AreasField
                      companyId={companyId}
                      employeeId={employeeId}
                      value={field.value}
                      onChange={field.onChange}
                      isEditable={isEditable && !isInheritingAdditionalArea}
                      isDisabled={isInheritingAdditionalArea}
                      label={__("Wyświetlany dodatkowy obszar")}
                      error={errors.additionalArea}
                    />
                  )}
                />

                {errors.additionalArea && (
                  <Typography
                    color="error"
                    sx={{
                      marginY: 1,
                      fontSize: "14px",
                      lineHeight: "21px",
                    }}
                  >
                    {__(errors.additionalArea.message)}
                  </Typography>
                )}

                <Typography
                  sx={{
                    fontSize: "inherit",
                    fontFamily: "inherit",
                  }}
                >
                  {__(
                    "Wyświetlany dodatkowy obszar będzie pokazany na karcie pracownika jako miejsce jego przynależności w strukturze organizacji obok obszaru, do którego należy. Przykład: Zespół kadr, Kadry i płace."
                  )}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Controller
                  name="inheritSupervisor"
                  control={control}
                  render={({ field }) => (
                    <ToggleSwitch
                      disabled={
                        !isEditable ||
                        (!!areaWatch?.value &&
                          !currentAreaMetadata?.givenEmployeeCanInheritManager)
                      }
                      checked={field.value}
                      handleChange={field.onChange}
                      label={__(
                        "Przełożonym jest zarządzający obszarem, do którego należy pracownik"
                      )}
                    />
                  )}
                />

                {areaWatch?.value &&
                  !currentAreaMetadata?.givenEmployeeCanInheritManager && (
                    <Alert color="warning">
                      <img
                        style={{ marginRight: "12px" }}
                        src={WarningIcon}
                        alt={__("warning")}
                      />
                      {`${__(
                        "Nie można wybrać tej opcji, ponieważ zarządzający obszarem"
                      )} ${areaWatch?.label} ${__(
                        "jest podwładnym tego pracownika."
                      )}`}
                    </Alert>
                  )}

                <Alert color="info">
                  <img
                    style={{ marginRight: "12px" }}
                    src={InfoIcon}
                    alt={__("info")}
                  />
                  {`${__(
                    "W przypadku, gdy pracownik zarządza obszarem, którego jest członkiem, jego przełożonym jest zarządzający nadrzędnym obszarem."
                  )}`}
                </Alert>

                <Controller
                  name="supervisor"
                  control={control}
                  render={({ field }) => (
                    <SupervisorsField
                      employeeId={employeeId}
                      companyId={companyId}
                      structureName={structureName}
                      value={field.value}
                      onChange={field.onChange}
                      isEditable={isEditable}
                      isDisabled={isInheritingSupervisor}
                    />
                  )}
                />
              </Box>
            </FormGroup>
          </Stack>
        </Box>
      </Box>
    </>
  );
};

export default OrganizationStructureForm;
